/* Shared member-app atoms per gympe-app-design/COMPONENTS.md.
   All style values come from design/member.css variables — never hardcode. */
import { ReactNode, useEffect, useId, useRef } from 'react';
import Icon from './Icon';

// ── Progress ring ────────────────────────────────────────────────────────────
export function Ring({
  v,
  size = 76,
  w = 7,
  track,
  children,
}: {
  v: number; // 0-100
  size?: number;
  w?: number;
  track?: string;
  children?: ReactNode;
}) {
  const id = useId();
  const r = (size - w) / 2;
  const C = 2 * Math.PI * r;
  const clamped = Math.max(0, Math.min(100, v));
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: `0 0 ${size}px` }}>
      <svg className="ring" width={size} height={size}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" />
            <stop offset="1" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke={track ?? 'var(--accent-fill)'} strokeWidth={w} />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={`url(#${id})`} strokeWidth={w} strokeDasharray={`${(C * clamped) / 100} ${C}`} />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}

// ── Sparkline / area chart ───────────────────────────────────────────────────
export function Spark({
  values,
  h = 32,
  w = 84,
  fill = false,
}: {
  values: number[];
  h?: number;
  w?: number;
  fill?: boolean;
}) {
  if (values.length < 2) return <div style={{ height: h }} />;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const pad = 3;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = pad + (1 - (v - min) / span) * (h - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg className="spark" width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" fill="none">
      {fill && <polygon points={`${pts} ${w},${h} 0,${h}`} />}
      <polyline points={pts} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Gympe lock-up (never tenant-themed) ──────────────────────────────────────
export function GympeWordmark() {
  return (
    <span className="gympe">
      Gym<b>pe</b>
    </span>
  );
}

export function PoweredByGympe() {
  return (
    <span className="powered">
      Powered by <GympeWordmark />
    </span>
  );
}

export function GympePill({ version }: { version?: string }) {
  return (
    <span className="gympe-pill">
      Powered by <GympeWordmark />
      {version && <span style={{ letterSpacing: '.06em' }}>{version}</span>}
    </span>
  );
}

// ── Tenant gym header ────────────────────────────────────────────────────────
export function GymHeader({
  name,
  logoUrl,
  right,
  onBrandClick,
}: {
  name: string;
  logoUrl?: string | null;
  right?: ReactNode;
  /** Set when the member belongs to more than one gym — opens the switcher. */
  onBrandClick?: () => void;
}) {
  const brand = (
    <>
      <div className="mark">{logoUrl ? <img src={logoUrl} alt="" /> : name.charAt(0).toUpperCase()}</div>
      <div className="col" style={{ minWidth: 0, alignItems: 'flex-start' }}>
        <div className="trunc" style={{ fontSize: 13.5, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>
          {name}
        </div>
        <PoweredByGympe />
      </div>
      {onBrandClick && <Icon n="right" s={15} c="var(--ink-3)" />}
    </>
  );
  return (
    <div className="between">
      {onBrandClick ? (
        <button className="brand" style={{ textAlign: 'left' }} onClick={onBrandClick}>{brand}</button>
      ) : (
        <div className="brand">{brand}</div>
      )}
      {right}
    </div>
  );
}

// ── Bottom sheet ─────────────────────────────────────────────────────────────
export function Sheet({ onClose, children }: { onClose: () => void; children: ReactNode }) {
  // Lock background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);
  return (
    <>
      <div className="dim" onClick={onClose} />
      <div className="sheet" role="dialog" aria-modal="true">
        <div className="grab" />
        {children}
      </div>
    </>
  );
}

// ── Toggle ───────────────────────────────────────────────────────────────────
export function Toggle({ on, onChange }: { on: boolean; onChange: (v: boolean) => void }) {
  return (
    <button className={`toggle${on ? '' : ' off'}`} role="switch" aria-checked={on} onClick={() => onChange(!on)}>
      <i />
    </button>
  );
}

// ── Stepper (− value +) ──────────────────────────────────────────────────────
export function Stepper({
  value,
  step,
  onChange,
  format,
}: {
  value: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}) {
  const holdRef = useRef<number | null>(null);
  const bump = (dir: 1 | -1) => {
    const next = Math.max(0, parseFloat((value + dir * step).toFixed(step < 1 ? 1 : 0)));
    onChange(next);
  };
  useEffect(() => () => { if (holdRef.current) window.clearInterval(holdRef.current); }, []);
  const fmt = format ?? ((v: number) => (step < 1 ? v.toFixed(1) : String(Math.round(v))));
  return (
    <div className="step">
      <button aria-label="decrease" onClick={() => bump(-1)}>−</button>
      <b>{fmt(value)}</b>
      <button aria-label="increase" onClick={() => bump(1)}>+</button>
    </div>
  );
}

// ── Formatting helpers ───────────────────────────────────────────────────────

/** ₹ with Indian digit grouping: 120000 → ₹1,20,000 */
export function inr(amount: number): string {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
}

/** Money to the paisa, for amounts someone pays: 12980.5 → ₹12,980.50 */
export function inrExact(amount: number): string {
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** "06:30" → { time: "6:30", ampm: "AM" } */
export function splitTime(hhmm: string): { time: string; ampm: string } {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return { time: `${h12}:${String(m).padStart(2, '0')}`, ampm };
}

export function fmtTime(hhmm: string): string {
  const { time, ampm } = splitTime(hhmm);
  return `${time} ${ampm}`;
}

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function parseYmd(ymd: string): Date {
  return new Date(`${ymd}T00:00:00`);
}

/** "2026-07-25" → "Fri 25 Jul" */
export function fmtDateShort(ymd: string): string {
  const d = parseYmd(ymd);
  return `${DAY_SHORT[d.getDay()]} ${d.getDate()} ${MONTH_SHORT[d.getMonth()]}`;
}

/** "2026-07-25" → "Friday, 25 July" */
export function fmtDateLong(ymd: string): string {
  const d = parseYmd(ymd);
  const DAY_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${DAY_LONG[d.getDay()]}, ${d.getDate()} ${MONTH_LONG[d.getMonth()]}`;
}

/** Local device date as YYYY-MM-DD (members log in their own timezone). */
export function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function addDaysStr(ymd: string, days: number): string {
  const d = parseYmd(ymd);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Weekday letter for the kit's day pills: M T W T F S S */
export function dayLetter(ymd: string): string {
  return 'SMTWTFS'[parseYmd(ymd).getDay()];
}

export function greeting(): string {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}
